<template>
	<FormContainer
		class="form"
		title="Create App"
		:as-dialog
		:visible
		@close="createApp()">
		<form @submit.prevent="submit">
			<SelectButton
				v-model="toggleAIValue"
				class="form__ai-option-button"
				:allow-empty="false"
				:options="[...toggleAIOptions]" />
			<FormInput
				label="Name"
				required
				:error-message="formErrors.name">
				<InputText
					v-model="formData.name"
					fluid
					placeholder="App name"
					:disabled="!!props.forceValues.name" />
			</FormInput>
			<FormInput
				v-if="toggleAIValue === 'Manual'"
				label="Description"
				:error-message="formErrors.description">
				<template #default>
					<Textarea
						v-model="formData.description"
						:rows="8" />
				</template>
			</FormInput>
			<FormInput
				v-if="toggleAIValue === 'AI Assisted'"
				class="form__ai-prompt-label-container"
				label="AI Prompt"
				required
				:error-message="formErrors.ai_prompt">
				<template #label="slotProps">
					<label :for="slotProps.inputId">AI Prompt *</label>
					<p class="form__secondary-text">
						Describe an application, and AI will generate it for you. You can always edit it later.
					</p>
				</template>
				<template #default>
					<div class="form__textarea-container">
						<div
							v-if="!formData.ai_prompt"
							class="form__example-prompt-button-container">
							<Button
								v-for="(exampleText, key) in examplePrompts"
								:key
								class="form__example-prompt-button"
								severity="secondary"
								variant="outlined"
								:label="exampleText.name"
								@click="
									(formData.ai_prompt = exampleText.prompt) && (formData.name = exampleText.name)
								" />
						</div>
						<Textarea
							v-model="formData.ai_prompt"
							class="form_textarea"
							:rows="16">
						</Textarea>
					</div>
				</template>
			</FormInput>
			<FormInput
				v-if="toggleAIValue === 'Schema'"
				label="Schema"
				required
				:error-message="formErrors.schema">
				<template #default>
					<div class="form__textarea-container">
						<Textarea
							v-model="formData.schema"
							class="form_textarea"
							:rows="16">
						</Textarea>
					</div>
				</template>
			</FormInput>
			<p
				v-if="loading"
				class="form__secondary-text">
				This may take a few minutes
			</p>
			<Button
				icon="fal fa-save"
				type="submit"
				:label="isEdit ? 'Update' : 'Create'"
				:loading="loading"
				@submit.prevent="submit" />
		</form>
	</FormContainer>
</template>

<script setup lang="ts">
import { onBeforeMount, ref, toRef, watch } from 'vue'
import { useRouter } from 'vue-router'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import type { App, AppStorePayload } from '@/models/App/Model'
import AppsApi from '@/models/App/Api'
import FormContainer from '@/components/FormContainer.vue'
import FormInput from '@/components/FormInput.vue'
import SelectButton from 'primevue/selectbutton'
import { useForm } from '@/helpers/form'
import { usePostHog } from '@/composables/usePostHog.ts'
import { useAuthStore } from '@/stores/Auth'

type FormData = {
	name: string
	description: string
	ai_prompt: string
	schema?: string
}
const examplePrompts = {
	ex2: {
		prompt: 'Build me a management app for tracking expenses',
		name: 'Expense tracking app',
	},
	ex3: {
		prompt:
			'Design a hospital management system with patients, doctors, nurses, appointments, departments, treatments, medications, and billing',
		name: 'Hospital management system',
	},
	ex4: {
		prompt:
			'Design a property management tool for landlords to track rent payments and maintenance requests',
		name: 'Property management tool',
	},
	ex5: {
		prompt:
			'Build a supply chain management tool with manufacturers, suppliers, warehouses, products, shipments, orders, and tracking events',
		name: 'Supply chain management tool',
	},
	ex6: {
		prompt:
			'Build a research publication database with authors, papers, citations, journals, conferences, and affiliations',
		name: 'Research publication database',
	},
	ex7: {
		prompt:
			'Develop a film production management app with projects, scripts, cast, crew, shooting schedules, locations, budgets, and contracts',
		name: 'Production management app',
	},
}

type ToggleOptionsType = 'AI Assisted' | 'Manual' | 'Schema'
const toggleOptions = ref<Array<ToggleOptionsType>>(['AI Assisted', 'Manual'])

const emit = defineEmits<{
	(e: 'start-loading'): void
	(e: 'stop-loading'): void
	(e: 'close'): void
	(e: 'submit'): void
	(e: 'created'): void
	(e: 'updated'): void
}>()
const props = withDefaults(
	defineProps<{
		id?: App['id']
		hideInputs?: (keyof FormData)[]
		defaultValues?: Partial<FormData>
		forceValues?: Partial<FormData>
		shouldRedirect?: boolean
		attachTo?: Record<string, { method: 'associate' | 'syncWithoutDetaching'; id: string | number }>
		asDialog?: boolean
		visible?: boolean
		hideRemove?: boolean
	}>(),
	{
		id: undefined,
		hideInputs: () => [],
		defaultValues: () => ({}),
		forceValues: () => ({}),
		shouldRedirect: true,
		attachTo: undefined,
		asDialog: false,
		visible: false,
		hideRemove: false,
	},
)
const toggleAIValue = ref<ToggleOptionsType>('AI Assisted')
const toggleAIOptions = ref(toggleOptions)
const { posthog } = usePostHog()
const auth = useAuthStore()

const router = useRouter()

function createApp() {
	posthog.capture('application_created')
	emit('close')
}

const { formData, loading, formErrors, reset, submit, isEdit } = useForm({
	api: () => new AppsApi(),
	defaultData: (): FormData => ({
		name: '',
		description: '',
		ai_prompt: '',
		schema: undefined,
	}),
	formatOnCreate: (data): AppStorePayload => {
		if (toggleAIValue.value === 'Manual') {
			return { name: data.name, creation_mode: 'manual', description: data.description }
		}
		if (toggleAIValue.value === 'Schema') {
			return { name: data.name, creation_mode: 'schema', schema: data.schema ?? '' }
		}
		return { name: data.name, creation_mode: 'ai', ai_prompt: data.ai_prompt }
	},
	forceValues: () => props.forceValues,
	attachTo: () => props.attachTo,
	id: toRef(props, 'id'),
	onStartLoading: () => emit('start-loading'),
	onStopLoading: () => emit('stop-loading'),
	onSubmit: () => emit('submit'),
	onCreated: (entity) => {
		if (props.shouldRedirect) {
			router.push({ name: 'apps-details', params: { id: entity!.id } })
		}
		emit('created')
	},
	onUpdated: () => emit('updated'),
})

watch(
	() => props.visible,
	(val) => {
		if (!val) reset()
	},
)

watch(toggleAIValue, () => {
	if (toggleAIValue.value === 'Manual' && formData.value.ai_prompt) {
		formData.value.description = formData.value.ai_prompt
		formData.value.ai_prompt = ''
	} else if (toggleAIValue.value === 'AI Assisted' && formData.value.description) {
		formData.value.ai_prompt = formData.value.description
		formData.value.description = ''
	}
})

onBeforeMount(() => {
	if (auth.user?.role === 'admin') {
		toggleOptions.value.push('Schema')
	}
})
</script>

<style scoped lang="scss">
.form {
	form {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 10px;

		& > * {
			width: 100%;
		}

		.form__footer-container {
			display: flex;
			justify-content: flex-end;
			align-items: center;
			gap: 10px;
		}

		&--edit {
			.form__footer-container {
				justify-content: space-between;
			}
		}
		.form__ai-option-button {
			width: 100%;
			& :deep(.p-togglebutton) {
				width: 50%;
			}
		}

		.form__secondary-text {
			font-size: 13px;
			font-style: italic;
			color: var(--p-text-muted-color);
		}
		.form__textarea-container {
			position: relative;

			.form_textarea {
				width: 100%;
			}

			.form__example-prompt-button-container {
				display: grid;
				position: absolute;
				left: 5px;
				bottom: 10px;
				width: calc(100% - 10px);
				grid-template-columns: 1fr 1fr;
				grid-template-rows: 1fr 1fr 1fr;
				gap: 5px;
				align-items: stretch;

				.form__example-prompt-button {
					justify-content: flex-start;
					width: 100%;
					text-align: left;

					:deep(.p-button-label) {
						font-weight: 400;
					}
				}
			}
		}

		.form__ai-prompt-label-container {
			:deep(.form-input__label-container) {
				display: flex;
				flex-direction: column;
				gap: 0;
			}
		}
	}
}
</style>
